import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { TimerRenderer } from "react-use-precision-timer";
import cn from "../../../utils/cn";
import { ClassHrBoardSnapshot, LiveZone } from "../../../types";
import calcCalories from "../../../utils/calories";
import interpolate from "../../../utils/interpolate";
import { useAuthContext } from "../../../context/auth";
import { Pause, Play } from "../../Icons";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import BottomNav from "../BottomNav";

// 코인 운영 지점의 WORK OUT 화면(웨이트·부스터) — 세트 진행 + 실시간 심박 회원 카드.

const heartIcon = "/heart-rate/heart.svg";

// 한 화면에 12명. 더 많으면 8초마다 다음 12명으로 넘긴다.
const MEMBERS_PER_PAGE = 12;
const PAGE_INTERVAL_MS = 8000;

// 회원 카드 기본 크기와 간격. 인원이 적으면 카드 디자인은 그대로 두고 비율만 키운다.
const CARD_WIDTH = 282;
const CARD_HEIGHT = 183;
const CARD_GAP = 24;
const MAX_COLUMNS = 6;
const MAX_ROWS = 2;

/** 한 화면 인원(count)을 영역(width×height)에 가장 크게 놓는 열 수와 배율. 배율은 1보다 작아지지 않는다. */
function cardLayout(count: number, width: number, height: number) {
  let best = { columns: MAX_COLUMNS, scale: 1 };
  if (count <= 0 || width <= 0 || height <= 0) return best;
  let bestScale = 0;
  for (let columns = 1; columns <= MAX_COLUMNS; columns++) {
    const rows = Math.ceil(count / columns);
    if (rows > MAX_ROWS) continue;
    const scale = Math.min(
      (width - CARD_GAP * (columns - 1)) / columns / CARD_WIDTH,
      (height - CARD_GAP * (rows - 1)) / rows / CARD_HEIGHT,
    );
    if (scale > bestScale) {
      bestScale = scale;
      best = { columns, scale };
    }
  }
  return { columns: best.columns, scale: Math.max(1, best.scale) };
}

// 회원 카드 배경에 깔리는 존 색(15%). 패널 기본 배경 위에 겹친다.
const ZONE_TINT: Record<LiveZone, string> = {
  1: "rgba(165,107,215,0.15)",
  2: "rgba(60,177,219,0.15)",
  3: "rgba(88,198,19,0.15)",
  4: "rgba(255,153,10,0.15)",
  5: "rgba(229,72,77,0.15)",
};

const ZONE_COLOR: Record<LiveZone, string> = {
  1: "text-[#A56BD7]",
  2: "text-[#3CB1DB]",
  3: "text-[#58C613]",
  4: "text-[#FF990A]",
  5: "text-[#E5484D]",
};

const ZONE_BORDER: Record<LiveZone, string> = {
  1: "border-[rgba(165,107,215,0.48)]",
  2: "border-[rgba(60,177,219,0.48)]",
  3: "border-[rgba(88,198,19,0.48)]",
  4: "border-[rgba(255,153,10,0.48)]",
  5: "border-[rgba(229,72,77,0.48)]",
};

/**
 * 화면별 크기. 회원용 모니터(did 1)는 누를 일이 없어 하단 바를 빼고 위쪽(세트·타이머·알림)을
 * 화면 절반 가까이 키운다. 컨트롤 화면(did 0)은 단계 탭·하단 바가 있어 그만큼만 키운다.
 */
export function useCoinBoardSize() {
  const { did } = useAuthContext();
  const isMember = did === "1";
  return {
    isMember,
    /** 웨이트 세트 바(4개) 높이 */
    setBarHeight: isMember ? 96 : 66,
    /** 부스터 세트 바(3개, 위에 Easy·Normal·Hard 줄이 있다) 높이 */
    boosterBarHeight: isMember ? 72 : 58,
    setLabelClass: isMember ? "text-[48px]" : "text-[40px]",
  };
}

/** 세트 이름(1SET…) 칸 너비. 부스터의 Easy·Normal·Hard 줄도 이만큼 비우고 시작한다. */
export const SET_LABEL_WIDTH = 130;
export const SET_LABEL_GAP = 28;

function MidLine({ height }: { height: number }) {
  return (
    <svg width="4" height={height} viewBox={`0 0 4 ${height}`} fill="none">
      <line
        x1="2"
        y1="0"
        x2="2"
        y2={height}
        stroke="url(#coin_set_mid_line)"
        strokeWidth="4"
        strokeDasharray="6 6"
      />
      <defs>
        <linearGradient
          id="coin_set_mid_line"
          x1="0"
          y1="0"
          x2="-2"
          y2="0.04"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E3FAFF" />
          <stop offset="1" stopColor="#53CAF5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function CoinSetBar({
  number,
  count,
  time,
  started,
}: {
  number: number;
  count: number;
  time: number;
  started: boolean;
}) {
  const { setBarHeight: height, setLabelClass } = useCoinBoardSize();
  // 테두리(4)·안쪽 여백(2)을 빼고 위아래로 2씩 띄운 채움 높이.
  const fill = height - 16;
  return (
    <div className="flex items-center w-full" style={{ gap: SET_LABEL_GAP }}>
      <h1
        className={cn("shrink-0 text-white", setLabelClass)}
        style={{ width: SET_LABEL_WIDTH }}
      >
        {number}SET
      </h1>
      {started ? (
        <div
          className="flex-1 rounded-[100px] p-[4px] relative"
          style={{
            height,
            backgroundImage: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
          }}
        >
          <div className="absolute top-[1px] left-1/2 -translate-x-1/2 z-10 flex">
            <MidLine height={height - 4} />
          </div>
          <div className="w-full h-full bg-bg rounded-[100px] p-[2px] flex items-center">
            <div
              className="rounded-[100px] flex justify-end"
              style={{
                height: fill,
                backgroundImage:
                  "linear-gradient(270deg, #53CAF5 0%, rgba(83, 202, 245, 0) 100%)",
                transition: "all 0.5s ease",
                // 기존 WORK OUT 화면과 같은 채움 비율(10%에서 시작).
                width: `${interpolate((count / time) * 100)}%`,
                minWidth: fill,
              }}
            >
              <div
                className="rounded-[100px]"
                style={{
                  width: fill,
                  height: fill,
                  backgroundImage:
                    "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex-1 rounded-[100px] bg-[#1F2838]"
          style={{ height }}
        />
      )}
    </div>
  );
}

const panelClass =
  "bg-[rgba(255,255,255,0.12)] shadow-[0px_2px_8px_0px_rgba(177,177,177,0.25)]";

function MemberCard({
  member,
  kcal,
}: {
  member: ClassHrBoardSnapshot["members"][number];
  kcal: number;
}) {
  // 워치 연동(측정 세션)이 없는 회원은 이름만 보여준다. 카드 높이는 다른 카드와 맞춘다.
  const isWatchConnected = member.watchType !== null;
  return (
    <div
      className={cn(
        panelClass,
        // 존이 바뀌거나 측정값이 없어져도 카드 크기·내용 위치가 흔들리지 않게 테두리 자리를 항상 둔다.
        "flex flex-col gap-[16px] p-[16px] rounded-[12px] border-4 border-transparent",
        member.zone ? ZONE_BORDER[member.zone] : undefined,
      )}
      style={{
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        backgroundImage: member.zone
          ? `linear-gradient(${ZONE_TINT[member.zone]}, ${ZONE_TINT[member.zone]})`
          : undefined,
      }}
    >
      <div className="flex items-center justify-between h-[36px]">
        <div className="flex items-center gap-[16px] text-[28px]">
          <p className="text-[#8E8E93]">{member.order}</p>
          <p className="text-white">{member.name ?? "회원"}</p>
        </div>
      </div>
      <div
        className={cn(
          "flex items-center gap-[16px] h-[46px]",
          !isWatchConnected && "invisible",
        )}
      >
        <img src={heartIcon} alt="" width={46} height={46} />
        <div className="flex-1 flex items-end justify-between text-white leading-none">
          <p className="text-[46px]">{member.bpm ?? "-"}</p>
          <p className="text-[28px] py-[4px]">
            {member.percent !== null ? `${member.percent}%` : "-"}
          </p>
        </div>
      </div>
      <div
        className={cn(
          "flex items-center justify-between h-[29px] text-[24px] text-white",
          !isWatchConnected && "invisible",
        )}
      >
        <p className={member.zone ? ZONE_COLOR[member.zone] : undefined}>
          {member.zone ? `Z${member.zone}` : "-"}
        </p>
        <div className="flex items-center gap-[4px]">
          <p>{kcal}</p>
          <p className="text-[18px]">kcal</p>
        </div>
        <p>
          {member.expectedPoint !== null ? `+${member.expectedPoint}P` : "-"}
        </p>
      </div>
    </div>
  );
}

export function CoinMembers({
  members,
  weights,
  elapsedMinutes,
}: {
  members: ClassHrBoardSnapshot["members"];
  // userId → 체중(kg). 없으면 기본 체중으로 칼로리를 계산한다.
  weights: Record<number, number | null>;
  elapsedMinutes: number;
}) {
  const pageCount = Math.max(1, Math.ceil(members.length / MEMBERS_PER_PAGE));
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (pageCount <= 1) return;
    const interval = setInterval(
      () => setPage((prev) => (prev + 1) % pageCount),
      PAGE_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, [pageCount]);

  // 수업 중 인원이 줄어 페이지가 사라지면 남은 마지막 페이지를 보여준다.
  const currentPage = Math.min(page, pageCount - 1);
  const shown = members.slice(
    currentPage * MEMBERS_PER_PAGE,
    (currentPage + 1) * MEMBERS_PER_PAGE,
  );

  // 카드 영역 크기를 재서 인원에 맞는 배치를 고른다.
  const areaRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const update = () =>
      setArea({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { columns, scale } = cardLayout(shown.length, area.width, area.height);
  // 기본 크기(6열)는 기존처럼 양 끝을 맞추고, 키운 카드는 가운데로 모은다.
  const isBase = scale === 1;

  return (
    <div className="flex-1 min-h-0 px-[60px]">
      <div
        ref={areaRef}
        className="grid h-full content-start"
        style={{
          gridTemplateColumns: `repeat(${columns}, ${CARD_WIDTH * scale}px)`,
          justifyContent: isBase ? "space-between" : "center",
          columnGap: isBase ? undefined : CARD_GAP,
          rowGap: CARD_GAP,
        }}
      >
        {shown.map((member) => (
          <div
            key={member.userId}
            style={{ width: CARD_WIDTH * scale, height: CARD_HEIGHT * scale }}
          >
            <div
              style={{ transform: `scale(${scale})`, transformOrigin: "0 0" }}
            >
              <MemberCard
                member={member}
                kcal={calcCalories(weights[member.userId], elapsedMinutes)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** 수업 경과 시간(하단 바와 같은 값). 하단 바가 없는 회원용 모니터에서 타이머 칸에 보인다. */
function ClassElapsed() {
  const { timer } = useAuthContext();
  return (
    <div className="flex items-center gap-[16px] mt-[18px] text-[44px] text-[#9FDCF3]">
      <svg width="44" height="44" viewBox="0 0 68 68" fill="none">
        <circle cx="34" cy="38" r="25" stroke="#9FDCF3" strokeWidth="5" />
        <path
          d="M26 6H42M34 37L42 46M34 37V24"
          stroke="#9FDCF3"
          strokeWidth="5"
          strokeLinecap="round"
        />
      </svg>
      <TimerRenderer
        timer={timer}
        renderRate={500}
        render={(t) => {
          const sec = Math.floor(t.getElapsedRunningTime() / 1000);
          return (
            <p>
              수업 {String(Math.floor(sec / 60)).padStart(2, "0")}:
              {String(sec % 60).padStart(2, "0")}
            </p>
          );
        }}
      />
    </div>
  );
}

/**
 * 라운드·남은 시간 칸. 컨트롤 화면은 일시정지 버튼을, 회원용 모니터는 누를 사람이 없으니
 * 그 자리에 수업 경과 시간을 보인다.
 */
export function CoinTimerPanel({
  round,
  remaining,
  stopped,
  onToggle,
}: {
  round: string;
  remaining: string;
  stopped: boolean;
  onToggle: () => void;
}) {
  const { isMember } = useCoinBoardSize();
  return (
    <div className="flex flex-col items-center justify-center w-[500px] shrink-0 rounded-[24px] bg-[rgba(255,255,255,0.12)] text-white">
      <h2 className={cn("leading-[1.2]", isMember ? "text-[72px]" : "text-[60px]")}>
        {round}
      </h2>
      <h2
        className={cn(
          "leading-none font-['TheJamsil6ExtraBold']",
          isMember ? "text-[140px]" : "text-[124px]",
        )}
      >
        {remaining}
      </h2>
      {isMember ? (
        <ClassElapsed />
      ) : (
        <button className="mt-[16px]" onClick={onToggle}>
          {stopped ? <Play /> : <Pause />}
        </button>
      )}
    </div>
  );
}

/**
 * 실시간 심박 WORK OUT 화면 틀(웨이트·부스터 공통). 위쪽에 세트·타이머, 아래에 회원 카드.
 * 회원용 모니터는 하단 바(첫 화면으로·화살표·수업 시간)를 빼고 그 높이를 카드 영역에 준다.
 */
export function CoinBoardScreen({
  sets,
  timer,
  members,
}: {
  sets: React.ReactNode;
  timer: React.ReactNode;
  members: React.ReactNode;
}) {
  const { isMember } = useCoinBoardSize();
  return (
    <LandscapeProgramLayout step={4}>
      {/* 컨트롤 화면은 BottomNav 의 빈 칸(flex-1)보다 크게 늘어나서 남는 높이를 카드 영역이 쓴다. */}
      <div
        className={cn(
          "grow-[100] basis-0 min-h-0 flex flex-col",
          isMember
            ? "gap-[42px] pt-[48px] pb-[44px]"
            : "gap-[26px] pt-[32px] pb-[24px]",
        )}
      >
        <div
          className={cn(
            "flex items-stretch gap-[40px] px-[60px] shrink-0",
            isMember ? "h-[550px]" : "h-[390px]",
          )}
        >
          <div className="flex-1 min-w-0 flex flex-col">{sets}</div>
          {timer}
        </div>
        {members}
      </div>
      {!isMember && <BottomNav />}
    </LandscapeProgramLayout>
  );
}
